import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { useAuctionState, useAuctionBids } from 'dibs-auctions/exports/hooks';
import { useOriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { trackAuctionBidClick } from 'dibs-auctions-bidding-modal/exports/bidModalTrackingHelpers';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { useAppSelector } from '../hooks/useReactReduxHooks';

// components
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';

import { AUCTION_BID_MODAL_TYPE } from '../hooks/useAuctionBidModalState';

// types
import { type PdpSharedCondensedAuctionButtons_item$key } from './__generated__/PdpSharedCondensedAuctionButtons_item.graphql';

type Props = {
    item: PdpSharedCondensedAuctionButtons_item$key;
};

export const PdpSharedCondensedAuctionButtons: FC<Props> = ({ item: itemRef }) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedCondensedAuctionButtons_item on Item {
                ...useAuctionState_item @arguments(page: pdp)
                ...useOriginalListPrice_item @arguments(page: pdp)
                ...useAuctionBids_item @arguments(fetchUser: $fetchUser)
                ...bidModalTrackingHelpers_item
                pdpURL
            }
        `,
        itemRef
    );

    const userId = useAppSelector(relay => relay?.relayVariables?.variables?.userId);

    const { currency } = useCurrency();
    const { isActiveAuction } = useAuctionState({ item });
    const { hasBid } = useAuctionBids({ item, userId, currency });
    const { hasOriginalListPrice } = useOriginalListPrice({ item, currency });

    const pdpURL = item?.pdpURL || '';

    const handlePlaceBidClick = (): void => {
        trackAuctionBidClick({ item, hasBid, hasOriginalListPrice, isCondensed: true });
        window.location.assign(
            handleLocaleUrl(`${pdpURL}?modal=${AUCTION_BID_MODAL_TYPE}`, GLOBAL_CLIENT_ONLY_LOCALE)
        );
    };

    if (!isActiveAuction) {
        return null;
    }

    return (
        <PdpSharedAuctionBidButton
            onClick={() => handlePlaceBidClick()}
            size="medium"
            type="transparent"
        />
    );
};
